import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Dimensions } from 'react-native';
import { Text, Card, useTheme, Chip, ProgressBar } from 'react-native-paper';
import {
  TrendingUp,
  Users,
  MessageCircle,
  Clock,
  Star,
  Target,
  BarChart3,
  PieChart,
  Activity,
  Zap,
} from 'lucide-react-native';
import type { PeerMatchingAnalytics } from '../../store/slices/peerMatchingSlice';

interface PeerMatchingAnalyticsProps {
  analytics: PeerMatchingAnalytics;
  showDetailedBreakdown?: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<any>;
  color: string;
  trend?: {
    direction: 'up' | 'down' | 'stable';
    value: number;
    label: string;
  };
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
  trend,
}: MetricCardProps) {
  const theme = useTheme();

  const getTrendColor = (direction: string) => {
    switch (direction) {
      case 'up':
        return '#4ECDC4';
      case 'down':
        return '#E74C3C';
      default:
        return theme.colors.onSurfaceVariant;
    }
  };

  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'up':
        return '↗';
      case 'down':
        return '↘';
      default:
        return '→';
    }
  };

  return (
    <Card
      style={[styles.metricCard, { backgroundColor: theme.colors.surface }]}
    >
      <Card.Content style={styles.metricContent}>
        <View style={styles.metricHeader}>
          <Icon size={20} color={color} />
          <Text
            variant='bodySmall'
            style={[
              styles.metricTitle,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            {title}
          </Text>
        </View>

        <Text
          variant='headlineSmall'
          style={[styles.metricValue, { color: theme.colors.onSurface }]}
        >
          {value}
        </Text>

        {subtitle && (
          <Text
            variant='bodySmall'
            style={[
              styles.metricSubtitle,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            {subtitle}
          </Text>
        )}

        {trend && (
          <View style={styles.trendContainer}>
            <Text
              style={[
                styles.trendText,
                { color: getTrendColor(trend.direction) },
              ]}
            >
              {getTrendIcon(trend.direction)} {trend.value}% {trend.label}
            </Text>
          </View>
        )}
      </Card.Content>
    </Card>
  );
}

export function PeerMatchingAnalytics({
  analytics,
  showDetailedBreakdown = true,
}: PeerMatchingAnalyticsProps) {
  const theme = useTheme();
  const [selectedTimeframe, setSelectedTimeframe] = useState<
    'week' | 'month' | 'all'
  >('month');

  // Calculate derived metrics
  const connectionRate =
    analytics.totalMatches > 0
      ? (analytics.successfulConnections / analytics.totalMatches) * 100
      : 0;

  const averageResponseHours = Math.round(analytics.averageResponseTime / 60);

  // Mock trend data (in a real app, this would come from the API)
  const getTrendData = () => ({
    matches: { direction: 'up' as const, value: 12, label: 'vs last month' },
    connections: { direction: 'up' as const, value: 8, label: 'vs last month' },
    responseTime: {
      direction: 'down' as const,
      value: 15,
      label: 'improvement',
    },
    engagement: { direction: 'up' as const, value: 5, label: 'vs last month' },
  });

  const trends = getTrendData();

  // Get success rate color
  const getSuccessRateColor = (rate: number) => {
    if (rate >= 70) return '#4ECDC4';
    if (rate >= 50) return '#FF6B35';
    return '#E74C3C';
  };

  // Get engagement score color
  const getEngagementColor = (score: number) => {
    if (score >= 0.8) return '#4ECDC4';
    if (score >= 0.6) return '#FF6B35';
    return '#E74C3C';
  };

  const renderTimeframeSelector = () => (
    <View style={styles.timeframeSelector}>
      {(['week', 'month', 'all'] as const).map(timeframe => (
        <Chip
          key={timeframe}
          mode={selectedTimeframe === timeframe ? 'flat' : 'outlined'}
          selected={selectedTimeframe === timeframe}
          onPress={() => setSelectedTimeframe(timeframe)}
          style={styles.timeframeChip}
          textStyle={styles.timeframeChipText}
        >
          {timeframe === 'week'
            ? 'This Week'
            : timeframe === 'month'
              ? 'This Month'
              : 'All Time'}
        </Chip>
      ))}
    </View>
  );

  const renderOverviewMetrics = () => (
    <View style={styles.metricsGrid}>
      <MetricCard
        title='Total Matches'
        value={analytics.totalMatches}
        subtitle='AI-generated matches'
        icon={Users}
        color='#4ECDC4'
        trend={trends.matches}
      />

      <MetricCard
        title='Connections Made'
        value={analytics.successfulConnections}
        subtitle={`${Math.round(connectionRate)}% success rate`}
        icon={MessageCircle}
        color='#FF6B35'
        trend={trends.connections}
      />

      <MetricCard
        title='Avg Response Time'
        value={`${averageResponseHours}h`}
        subtitle='Time to respond'
        icon={Clock}
        color='#9B59B6'
        trend={trends.responseTime}
      />

      <MetricCard
        title='Engagement Score'
        value={`${Math.round(analytics.userEngagementScore * 100)}%`}
        subtitle='Your activity level'
        icon={Activity}
        color={getEngagementColor(analytics.userEngagementScore)}
        trend={trends.engagement}
      />
    </View>
  );

  const renderCompatibilityBreakdown = () => (
    <Card style={[styles.section, { backgroundColor: theme.colors.surface }]}>
      <Card.Content>
        <Text
          variant='titleMedium'
          style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
        >
          Match Quality Analysis
        </Text>

        <View style={styles.qualityMetric}>
          <View style={styles.qualityHeader}>
            <Star size={16} color='#FFD700' />
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface }}
            >
              Average Compatibility Score
            </Text>
          </View>
          <View style={styles.qualityScore}>
            <Text
              variant='headlineSmall'
              style={[styles.scoreValue, { color: theme.colors.onSurface }]}
            >
              {Math.round(analytics.averageCompatibilityScore * 100)}%
            </Text>
            <ProgressBar
              progress={analytics.averageCompatibilityScore}
              color={getSuccessRateColor(
                analytics.averageCompatibilityScore * 100
              )}
              style={styles.scoreProgress}
            />
          </View>
        </View>

        <View style={styles.qualityMetric}>
          <View style={styles.qualityHeader}>
            <Target size={16} color={theme.colors.primary} />
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurface }}
            >
              Connection Success Rate
            </Text>
          </View>
          <View style={styles.qualityScore}>
            <Text
              variant='headlineSmall'
              style={[styles.scoreValue, { color: theme.colors.onSurface }]}
            >
              {Math.round(analytics.connectionSuccessRate * 100)}%
            </Text>
            <ProgressBar
              progress={analytics.connectionSuccessRate}
              color={getSuccessRateColor(analytics.connectionSuccessRate * 100)}
              style={styles.scoreProgress}
            />
          </View>
        </View>
      </Card.Content>
    </Card>
  );

  const renderMatchingFactors = () => (
    <Card style={[styles.section, { backgroundColor: theme.colors.surface }]}>
      <Card.Content>
        <Text
          variant='titleMedium'
          style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
        >
          Top Matching Factors
        </Text>
        <Text
          variant='bodySmall'
          style={[
            styles.sectionSubtitle,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          What makes your matches successful
        </Text>

        <View style={styles.factorsContainer}>
          {analytics.topMatchingFactors.map((factor, index) => {
            const importance = Math.max(0.3, 1 - index * 0.15); // Decreasing importance
            const factorColor =
              importance > 0.7
                ? '#4ECDC4'
                : importance > 0.5
                  ? '#FF6B35'
                  : '#9B59B6';

            return (
              <View key={factor} style={styles.factorItem}>
                <View style={styles.factorHeader}>
                  <Text
                    variant='bodyMedium'
                    style={[
                      styles.factorName,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    {factor
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[styles.factorScore, { color: factorColor }]}
                  >
                    {Math.round(importance * 100)}%
                  </Text>
                </View>
                <ProgressBar
                  progress={importance}
                  color={factorColor}
                  style={styles.factorProgress}
                />
              </View>
            );
          })}
        </View>
      </Card.Content>
    </Card>
  );

  const renderEngagementInsights = () => (
    <Card style={[styles.section, { backgroundColor: theme.colors.surface }]}>
      <Card.Content>
        <Text
          variant='titleMedium'
          style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
        >
          Engagement Insights
        </Text>

        <View style={styles.insightsContainer}>
          <View style={styles.insightItem}>
            <View style={styles.insightIcon}>
              <TrendingUp size={16} color='#4ECDC4' />
            </View>
            <View style={styles.insightContent}>
              <Text
                variant='bodyMedium'
                style={[styles.insightTitle, { color: theme.colors.onSurface }]}
              >
                Peak Activity Time
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Most connections happen between 7-9 PM
              </Text>
            </View>
          </View>

          <View style={styles.insightItem}>
            <View style={styles.insightIcon}>
              <Zap size={16} color='#FF6B35' />
            </View>
            <View style={styles.insightContent}>
              <Text
                variant='bodyMedium'
                style={[styles.insightTitle, { color: theme.colors.onSurface }]}
              >
                Response Speed Impact
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Faster responses increase connection rate by 40%
              </Text>
            </View>
          </View>

          <View style={styles.insightItem}>
            <View style={styles.insightIcon}>
              <MessageCircle size={16} color='#9B59B6' />
            </View>
            <View style={styles.insightContent}>
              <Text
                variant='bodyMedium'
                style={[styles.insightTitle, { color: theme.colors.onSurface }]}
              >
                Message Quality
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                Personal messages have 60% higher acceptance rate
              </Text>
            </View>
          </View>
        </View>
      </Card.Content>
    </Card>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderTimeframeSelector()}
      {renderOverviewMetrics()}
      {renderCompatibilityBreakdown()}

      {showDetailedBreakdown && (
        <>
          {renderMatchingFactors()}
          {renderEngagementInsights()}
        </>
      )}

      <View style={styles.lastUpdated}>
        <Text
          variant='bodySmall'
          style={[
            styles.lastUpdatedText,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          Last updated: {new Date(analytics.lastUpdated).toLocaleString()}
        </Text>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  timeframeSelector: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 20,
  },
  timeframeChip: {
    minWidth: 80,
  },
  timeframeChipText: {
    fontSize: 12,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },
  metricCard: {
    width: (Dimensions.get('window').width - 44) / 2, // Account for padding and gap
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  metricContent: {
    paddingVertical: 12,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  metricTitle: {
    fontSize: 12,
    fontWeight: '500',
  },
  metricValue: {
    fontWeight: '700',
    marginBottom: 4,
  },
  metricSubtitle: {
    fontSize: 11,
    marginBottom: 6,
  },
  trendContainer: {
    marginTop: 4,
  },
  trendText: {
    fontSize: 10,
    fontWeight: '600',
  },
  section: {
    marginBottom: 16,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  sectionSubtitle: {
    marginBottom: 16,
  },
  qualityMetric: {
    marginBottom: 20,
  },
  qualityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  qualityScore: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  scoreValue: {
    fontWeight: '600',
    minWidth: 60,
  },
  scoreProgress: {
    flex: 1,
    height: 6,
    borderRadius: 3,
  },
  factorsContainer: {
    gap: 16,
  },
  factorItem: {
    gap: 8,
  },
  factorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  factorName: {
    fontWeight: '500',
  },
  factorScore: {
    fontWeight: '600',
    fontSize: 12,
  },
  factorProgress: {
    height: 4,
    borderRadius: 2,
  },
  insightsContainer: {
    gap: 16,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  insightIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0,0,0,0.05)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  insightContent: {
    flex: 1,
  },
  insightTitle: {
    fontWeight: '500',
    marginBottom: 2,
  },
  lastUpdated: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  lastUpdatedText: {
    fontSize: 11,
    fontStyle: 'italic',
  },
});
